import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"

// One row of the provider's legal-counter panel (EU 561/2006): what the
// driver used, the limit in force right now and what is left. `limit` is a
// maximum for driving/amplitude/work-week rows and a minimum for rest and
// break rows. Seconds, except the day counters (extended driving days,
// reduced daily rests, consecutive driving days), which count days.
export type LegalCounter = {
  total: number | null
  limit: number | null
  remaining: number | null
}

// A past week's weekly rest as the provider judged it: how long it was, the
// minimum it had to be, the compensation still owed and where it was taken.
export type WeeklyRestRecord = {
  weeksAgo: number
  total: number | null
  limit: number | null
  remaining: number | null
  country: string | null
  atHome: boolean | null
  compensationDeadline: string | null
}

// The whole panel of one card, as the telematics provider computes it
// (Transics today). Not every provider has it - then it is absent.
export type LegalCounters = {
  continuousDriving: LegalCounter | null
  dailyDriving: LegalCounter | null
  dailyAmplitude: LegalCounter | null
  extendedDrivingDays: LegalCounter | null
  reducedDailyRestDays: LegalCounter | null
  weeklyDriving: LegalCounter | null
  biweeklyDriving: LegalCounter | null
  consecutiveDrivingDays: LegalCounter | null
  workWeek: LegalCounter | null
  workWeekEnd: string | null
  middayBreak: LegalCounter | null
  dailyRest: LegalCounter | null
  previousDailyRest: LegalCounter | null
  weeklyRestCompensation: LegalCounter | null
  weeklyRests: WeeklyRestRecord[]
}

// A member of a merged double-crew tacho: the panel is per card, so each
// member carries their own.
export type CrewMemberTacho = {
  driverId: string | null
  driverName: string | null
  legalCounters: LegalCounters | null
}

export type TachoReading = {
  remainingSecondsToday: number
  usedSecondsToday: number
  remainingMiddayBreakSeconds: number | null
  remainingBeforeDayStartBreakSeconds: number | null
  remainingAfterDayEndBreakSeconds: number | null
  remainingSecondsThisWeek: number | null
  remainingSecondsOfUninterruptedDriving: number | null
  remainingWeeklyRestSeconds: number | null
  remainingShortenedDailyRests: number | null
  remainingSecondsTwoWeeks: number | null
  remainingDailyExtensions: number | null
  usedSecondsThisWeek: number | null
  remainingSecondsNextWeek: number | null
  remainingDaysOfDriving: number | null
  weeklyRestRequiredSeconds: number | null
  weeklyRestInProgress: boolean
  currentActivity: string | null
  publishedAt: number | null
  dayEndAt: number | null
  weeklyRestDueAt: number | null
  shiftStartAt: number | null
  restStartedAt: number | null
  doubleCrew: boolean
  driverName: string | null
  legalCounters: LegalCounters | null
}

export type CrewMemberReading = TachoReading & {
  driverId: string | null
}

export type Tacho = {
  jid: string
  deviceId: string
  remainingSecondsToday: number
  usedSecondsToday: number
  remainingMiddayBreakSeconds: number | null
  remainingBeforeDayStartBreakSeconds: number | null
  remainingAfterDayEndBreakSeconds: number | null
  remainingSecondsThisWeek: number | null
  remainingSecondsOfUninterruptedDriving: number | null
  remainingWeeklyRestSeconds: number | null
  remainingShortenedDailyRests: number | null
  remainingSecondsTwoWeeks: number | null
  currentActivity: string | null
  timestamp: string | null
  dayEndTimestamp: string | null
  remainingDailyExtensions: number | null
  // GPS marks counters of a multi-manned vehicle (merged crew tacho, or a
  // member's own counter with the crew rules applied)
  doubleCrew?: boolean
  // Card holder's name as the provider reports it (vehicle rooms have no
  // driver accounts, so this is the only name a crew member has)
  driverName?: string | null
  legalCounters?: LegalCounters | null
  // Members of a merged crew tacho, each with their own legal counters
  crew?: CrewMemberTacho[]
  reading?: TachoReading
  crewReadings?: CrewMemberReading[]
}

export type TachoState = Record<string, Tacho>

const initialState: TachoState = {}

const publishedAt = (tacho: Tacho): number | null => {
  if (!tacho.timestamp) return null
  const time = new Date(tacho.timestamp).getTime()
  return Number.isNaN(time) ? null : time
}

// Tachos without a timestamp predate the GPS-side stamping; a stamped one
// always outranks them, and among stamped ones the later publish wins.
export const isNewerOrSame = (candidate: Tacho, current: Tacho): boolean => {
  const candidateTime = publishedAt(candidate)
  const currentTime = publishedAt(current)
  if (candidateTime === null) return currentTime === null
  if (currentTime === null) return true
  return candidateTime >= currentTime
}

type UpsertTachoAction = {
  tacho: Tacho
}

type BatchUpsertTachosAction = {
  tachos: Tacho[]
}

const tachos = createSlice({
  name: "tachos",
  initialState,
  reducers: {
    upsertTacho: (state, action: PayloadAction<UpsertTachoAction>) => {
      state[action.payload.tacho.jid] = action.payload.tacho
    },
    // The batch node keeps one item per gps_config id the GPS service ever
    // published under, and old items are not always gone: on accounts
    // sharing one telematics login (beedee.eu) the same driver appears in
    // several items with counters weeks apart. Whichever item the server
    // listed last used to win, which put 15.08 counters on a 16.09 chat.
    batchUpsertTachos: (
      state,
      action: PayloadAction<BatchUpsertTachosAction>,
    ) => {
      for (const tacho of action.payload.tachos) {
        const current = state[tacho.jid]
        if (current && !isNewerOrSame(tacho, current)) continue
        state[tacho.jid] = tacho
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { upsertTacho, batchUpsertTachos } = tachos.actions

export default tachos.reducer
